import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Play, BookOpen, ArrowRight, Star } from "lucide-react";
import { TEACHERS } from "../data/reciters";
import { SURAHS } from "../data/surahs";
import { Tx } from "../context/AutoTranslateContext";

/**
 * Learning-track landing page — the specific classical masters used to
 * STUDY tajweed and ahkam. Separate from the 20-reciter listening library.
 */
const TajweedTeachers = () => (
  <div data-testid="teachers-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
    <div className="flex items-center gap-2 text-xs text-mute mb-6">
      <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
      <span>/</span>
      <span className="text-forest"><Tx>Tajweed Teachers</Tx></span>
    </div>

    <div className="text-center mb-12">
      <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3"><Tx>Learn from the masters</Tx></p>
      <h1 className="font-heading text-4xl sm:text-5xl text-forest font-bold mb-4">
        <Tx>Tajweed Teachers</Tx>
      </h1>
      <p className="text-mute leading-relaxed max-w-2xl mx-auto">
        <Tx>These are the specific classical Egyptian and Madinan masters used worldwide to teach ahkām al-tilāwa — the rules of Qur'anic recitation. Recite along with each one directly inside the reader.</Tx>
      </p>
    </div>

    <div className="grid sm:grid-cols-2 gap-5">
      {TEACHERS.map((t) => (
        <article
          key={t.id}
          data-testid={`teacher-${t.id}`}
          className="clay-surface rounded-3xl p-6 flex flex-col"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-forest/10 text-forest flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="arabic text-gold text-xl leading-none mb-1">{t.arabic}</p>
              <h2 className="font-heading text-lg text-forest font-semibold">{t.name}</h2>
              <p className="text-[10px] uppercase tracking-widest text-mute mt-1">
                {t.country} · {t.style}
              </p>
            </div>
            <Star className="w-4 h-4 text-gold flex-shrink-0" />
          </div>

          <p className="text-sm text-ink/85 leading-relaxed mb-5 flex-1"><Tx>{t.bio}</Tx></p>

          <div className="flex flex-wrap gap-2">
            <Link
              data-testid={`teacher-fatiha-${t.id}`}
              to={`/academy/quran/1?reciter=${encodeURIComponent(t.id)}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-forest text-sand hover:bg-sage text-xs font-medium transition"
            >
              <Play className="w-3.5 h-3.5" /> <Tx>Al-Fatiha with this teacher</Tx>
            </Link>
            <Link
              data-testid={`teacher-mulk-${t.id}`}
              to={`/academy/quran/67?reciter=${encodeURIComponent(t.id)}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cream border border-forest/15 text-forest hover:border-forest text-xs transition"
            >
              <Play className="w-3.5 h-3.5" /> <Tx>Al-Mulk</Tx>
            </Link>
          </div>
        </article>
      ))}
    </div>

    <div className="mt-14 text-center bg-cream/60 rounded-3xl p-8 border border-gold/20">
      <BookOpen className="w-5 h-5 text-gold mx-auto mb-2" />
      <h3 className="font-heading text-xl text-forest font-semibold mb-2"><Tx>Not sure which teacher to start with?</Tx></h3>
      <p className="text-sm text-mute max-w-2xl mx-auto mb-4">
        <Tx>Start with Sheikh Mahmoud Khalil Al-Husary — his slow, deliberate murattal is the world reference for teaching tajweed. Once your rules are steady, move on to Al-Minshawi's mujawwad to learn the maqāmāt.</Tx>
      </p>
      <Link
        to="/academy/quran/1?reciter=Husary_128kbps"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-forest text-sand hover:bg-sage transition text-sm font-medium"
      >
        <Tx>Start with Al-Husary — Al-Fatiha</Tx>
        <ArrowRight className="w-4 h-4 rtl:rotate-180" />
      </Link>
    </div>

    <div className="mt-8 text-center">
      <Link
        to="/academy/quran/1"
        className="text-sm text-forest hover:underline"
      >
        <Tx>← Back to the Qur'an Reader</Tx>
      </Link>
    </div>
  </div>
);

export default TajweedTeachers;
